// Écran 3 — la quête quotidienne (séance en cours). Un tap par TOUR de circuit, pas par
// exercice ; un minuteur 40/20 pour les intervalles. L'état survit à la fermeture jusqu'à « Valider ».

import { useCallback, useEffect, useRef, useState } from 'react';

import type { BlocIntervalles, Ressenti, SeanceDuJour } from '@alfred/shared';
import type { PropsEcran } from '../App';
import { EnTeteRetour, Fenetre, Nav, Pastille } from '../components';
import {
  type EtatQuete, type Version, ajouterTour, blocsDe, chargerQuete, dureeMin, nouvelleQuete, retirerTour, sauverQuete, totaux, toursPrevus,
} from '../lib/quete';
import { VOCAB } from '../lib/theme';

function repos(r: number | number[]): string {
  return Array.isArray(r) ? r.join('–') : String(r);
}

function deuxChiffres(n: number): string {
  return String(Math.max(0, Math.floor(n))).padStart(2, '0');
}

/** Minuteur d'intervalles : effort / repos en boucle, exercice courant mis en avant. */
function Minuteur({ bloc, termine, onTermine, onExercice }: { bloc: BlocIntervalles; termine: boolean; onTermine: () => void; onExercice: (i: number) => void }) {
  const [ecoule, setEcoule] = useState(0); // secondes
  const [marche, setMarche] = useState(false);
  const depart = useRef(0);
  const total = bloc.duree_min * 60;
  const cycle = bloc.effort_s + bloc.repos_s;

  useEffect(() => {
    if (!marche) return;
    depart.current = Date.now() - ecoule * 1000;
    const t = window.setInterval(() => {
      const e = (Date.now() - depart.current) / 1000;
      setEcoule(Math.min(total, e));
      if (e >= total) {
        setMarche(false);
        onTermine();
      }
    }, 250);
    return () => window.clearInterval(t);
    // `ecoule` est volontairement lu une seule fois, au démarrage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marche]);

  const tour = Math.floor(ecoule / cycle);
  const dansCycle = ecoule - tour * cycle;
  const effort = dansCycle < bloc.effort_s;
  const reste = effort ? bloc.effort_s - dansCycle : cycle - dansCycle;
  useEffect(() => onExercice(tour % bloc.exercices.length), [tour, bloc.exercices.length, onExercice]);

  if (termine) return <p className="message accent" style={{ fontSize: 14 }}>[Bloc terminé]</p>;
  return (
    <div className="minuteur">
      <span className="phase" style={{ color: effort ? 'var(--sys-accent)' : 'var(--sys-muted)' }}>{marche || ecoule ? (effort ? 'EFFORT' : 'REPOS') : 'PRÊT'}</span>
      <span className="temps">{deuxChiffres(reste)}</span>
      <span className="mono muted" style={{ fontSize: 12 }}>{deuxChiffres((total - ecoule) / 60)}:{deuxChiffres((total - ecoule) % 60)} restantes</span>
      <div className="boutons" style={{ width: '100%', marginTop: 6 }}>
        <button className="btn principal" onClick={() => setMarche(!marche)}>{marche ? 'PAUSE' : ecoule ? 'REPRENDRE' : 'DÉMARRER'}</button>
        <button className="btn" onClick={onTermine}>Bloc terminé</button>
      </div>
    </div>
  );
}

/** La quête en cours, recopiée dans le stockage local à chaque changement. Sur le bureau, la notification et l'objectif la partagent. */
export function useQuete(s: SeanceDuJour | null): [EtatQuete | null, (q: EtatQuete | null) => void] {
  const [quete, setBrute] = useState<EtatQuete | null>(() => chargerQuete(s));
  const setQuete = useCallback((q: EtatQuete | null) => {
    sauverQuete(q);
    setBrute(q);
  }, []);
  return [quete, setQuete];
}

export function Quete({ etat, actions }: PropsEcran) {
  const s = etat.seance;
  const [quete, setQuete] = useQuete(s);

  return (
    <main className="ecran avec-nav">
      <EnTeteRetour titre={VOCAB.infoQuete} />
      {s ? (
        <ObjectifQuete etat={etat} actions={actions} s={s} quete={quete} setQuete={setQuete} apres={() => { window.location.hash = '#/'; }} />
      ) : (
        <Fenetre><p className="message">[Aucune séance au programme aujourd'hui.]</p></Fenetre>
      )}
      <Nav actif="quetes" />
    </main>
  );
}

/**
 * La fenêtre de l'objectif : blocs, tours, ressenti, puis « Valider ». `integre` : la variante du bureau, titre à
 * gauche, exercices sur deux colonnes et boutons dans la fenêtre (le message d'arrivée est déjà dans la notification).
 */
export function ObjectifQuete({ etat, actions, s, quete, setQuete, apres, integre = false }: PropsEcran & {
  s: SeanceDuJour;
  quete: EtatQuete | null;
  setQuete: (q: EtatQuete | null) => void;
  apres?: () => void;
  integre?: boolean;
}) {
  const [actif, setActif] = useState(0);
  const [envoi, setEnvoi] = useState(false);

  const version: Version = quete?.version ?? 'complete';
  const blocs = blocsDe(s, version, etat.versionCourte);
  const q = quete ?? nouvelleQuete(s, version, null);
  const { faits, prevus } = totaux(q, blocs);
  const consigne = `${s.seance.titre} · ${version === 'courte' ? `version ${etat.versionCourte?.dureeMin ?? 12} min` : `≈ ${s.seance.duree_min} min`}`;

  const changerVersion = () => setQuete({ ...nouvelleQuete(s, version === 'complete' ? 'courte' : 'complete', q.debut), tours: [] });
  const valider = async () => {
    if (!q.ressenti) return;
    setEnvoi(true);
    const duree = dureeMin(q, Date.now());
    const ok = await actions.seance(
      { date: s.date, lettre: s.lettre, version, ressenti: q.ressenti, ...(duree ? { dureeMin: duree } : {}) },
      `Quête accomplie : Séance ${s.lettre}.`,
    );
    setEnvoi(false);
    if (ok) {
      setQuete(null);
      apres?.();
    }
  };

  const boutons = !s.faite && (
    <div className="boutons">
      <button className="btn" style={{ flex: '0 0 auto', minHeight: integre ? 48 : 50 }} onClick={changerVersion}>
        {version === 'complete' ? 'Version 12 min' : 'Version complète'}
      </button>
      <button className="btn principal" style={{ minHeight: integre ? 48 : 50 }} disabled={!q.ressenti || envoi} onClick={() => void valider()}>
        {VOCAB.valider} · {faits} / {prevus}
      </button>
    </div>
  );

  return (
    <>
      <Fenetre className={integre ? 'objectif-integre' : ''}>
        {integre ? (
          <div className="objectif-tete">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="objectif">{VOCAB.objectif}</span>
              <span className="consigne">{etat.phase?.nom ?? 'Séance'} — Séance {s.lettre} · {consigne}</span>
            </div>
            <Pastille>{VOCAB.infoQuete}</Pastille>
          </div>
        ) : (
          <>
            <p className="message" style={{ fontSize: 14 }}>
              [{VOCAB.queteQuotidienne} : <strong>{etat.phase?.nom ?? 'Séance'} — Séance {s.lettre}</strong> {s.faite ? 'accomplie' : s.repli ? 'à rattraper' : 'est arrivée'}.]
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <span className="objectif">{VOCAB.objectif}</span>
              <span className="consigne">{consigne}</span>
            </div>
          </>
        )}

        {etat.echauffement && (
          <details>
            <summary>Échauffement · {version === 'courte' ? 3 : etat.echauffement.dureeMin} min</summary>
            {etat.echauffement.exercices.map((e) => (
              <div key={e.nom} className="exercice"><b>{e.nom}</b><small>{e.volume}</small></div>
            ))}
          </details>
        )}

        {blocs.map((b, i) => {
          const fait = Math.min(toursPrevus(b), q.tours[i] ?? 0);
          return (
            <section key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span className="consigne" style={{ textAlign: 'left', color: 'var(--sys-text-2)' }}>
                {b.type === 'circuit'
                  ? `Circuit · ${b.tours} tours · ${repos(b.repos_s)} s de repos entre les tours`
                  : `Intervalles · ${b.duree_min} min · ${b.effort_s} s effort / ${b.repos_s} s repos`}
              </span>
              <div className="exercices">
                {b.exercices.map((e, k) => (
                  <div key={e.nom + k} className={`exercice${b.type === 'intervalles' && k === actif && !fait ? ' actif' : ''}`}>
                    <b>{e.nom}</b>
                    <small>
                      {[e.volume, e.consigne].filter(Boolean).join(' · ')}
                      {e.cle && <span className="cle"> · {e.cle}</span>}
                    </small>
                  </div>
                ))}
              </div>
              {b.type === 'circuit' ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div className="tours" role="group" aria-label="Tours faits">
                    {Array.from({ length: b.tours }, (_, t) => (
                      <button
                        key={t}
                        className={`tour${t < fait ? ' fait' : ''}`}
                        aria-label={`Tour ${t + 1}${t < fait ? ' fait, toucher pour annuler' : ''}`}
                        // Seul le dernier tour fait s'annule : jamais de remise à zéro par accident.
                        onClick={() => t === fait - 1 && setQuete(retirerTour(q, i))}
                      >{t + 1}</button>
                    ))}
                  </div>
                  <button className="btn principal" style={{ fontSize: 13 }} disabled={fait >= b.tours || !!s.faite} onClick={() => setQuete(ajouterTour(q, i, b.tours, Date.now()))}>
                    TOUR FAIT [{fait} / {b.tours}]
                  </button>
                </div>
              ) : (
                <Minuteur bloc={b} termine={fait >= 1} onTermine={() => setQuete(ajouterTour(q, i, 1, Date.now()))} onExercice={setActif} />
              )}
            </section>
          );
        })}

        {!s.faite && (
          <div className="objectif-pied">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 280px' }}>
              <span className="muted" style={{ fontSize: 12 }}>Ressenti</span>
              <div className="ressentis">
                {(['facile', 'correct', 'dur'] as Ressenti[]).map((r) => (
                  <button key={r} aria-pressed={q.ressenti === r} onClick={() => setQuete({ ...q, ressenti: r })}>
                    {r === 'facile' ? 'Facile' : r === 'correct' ? 'Correct' : 'Dur'}
                  </button>
                ))}
              </div>
            </div>
            {integre && boutons}
          </div>
        )}

        <p className="avertissement">AVERTISSEMENT : douleur &gt; 3/10 ({etat.statut.debuffs.map((d) => d.titre.split(' ·')[0]?.toLowerCase()).join(', ') || 'toute zone'}) → passer à l'exercice suivant et le noter.</p>
      </Fenetre>

      {!integre && boutons}
    </>
  );
}
